import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useSite, usePageMeta } from "@/lib/content";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  company: "",
  event_name: "",
  event_date: "",
  venue: "",
  city: "",
  country: "",
  capacity: "",
  message: "",
  consent: false,
  website: "",
};

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="admin-label">
        {label}
        {required && <span className="text-bloodbright"> *</span>}
      </span>
      {children}
    </label>
  );
}

export default function BookingPage() {
  usePageMeta(
    "Booking — SEIMEN DEXTER | Book the artist",
    "Book SEIMEN DEXTER for your event — Hard / Industrial Techno, 165 BPM. Clubs, festivals, international bookings."
  );
  const { data: site } = useSite();
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/public/booking", form);
      setDone(data);
      window.scrollTo(0, 0);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (done) {
      window.scrollTo(0, 0);
    }
  }, [done]);

  if (done) {
    return (
      <div data-testid="booking-success" className="px-5 md:px-10 pt-28 md:pt-44 pb-28 md:pb-44 min-h-[70vh]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src="/assets/original_logo.png"
            alt="SEIMEN DEXTER"
            className="h-16 md:h-24 w-auto mb-10 object-contain"
            draggable="false"
          />
          <p className="kicker mb-6">BOOKING REQUEST</p>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.92]">
            BOOKING REQUEST SENT.
          </h1>
          <div className="mt-8 flex flex-col gap-1.5 text-sm md:text-base text-white/70 leading-relaxed">
            <p>Thank you for your request.</p>
            <p>Your booking request has been successfully submitted.</p>
            <p>We will get back to you as soon as possible.</p>
          </div>
          <p className="mt-10 text-sm text-white/50">
            Reference: <span className="text-white tracking-[0.2em]">{done.reference}</span>
          </p>
          <Link data-testid="booking-back-home" to="/" className="btn-primary mt-12 w-full sm:w-auto">
            BACK TO HOME
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-0 pb-24 md:pb-36">
      {/* CINEMATIC BOOKING HEADER */}
      <section className="relative overflow-hidden border-b border-line">
        <div className="absolute inset-0">
          <img
            src={fileUrl(site?.booking_image_url || "/assets/artist_live_03.webp")}
            alt="SEIMEN DEXTER in front of a rave crowd"
            fetchPriority="high"
            className="h-full w-full object-cover opacity-30"
            style={{ animation: "kenburns 24s ease-in-out infinite alternate" }}
            draggable="false"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/80" />
        </div>
        <div className="relative z-10 px-5 md:px-10 pt-36 md:pt-52 pb-16 md:pb-24">
          <img
            src="/assets/original_logo.png"
            alt="SEIMEN DEXTER"
            className="h-16 md:h-24 w-auto mb-10 object-contain"
            draggable="false"
          />
          <h1 className="font-display text-6xl md:text-8xl leading-[0.92]">
            {site?.booking_headline || "BOOK SEIMEN DEXTER"}
          </h1>
          <p className="mt-5 text-[11px] md:text-xs tracking-[0.3em] text-white/60">
            {site?.booking_subline || "CLUBS · FESTIVALS · INTERNATIONAL BOOKINGS"}
          </p>
          {site?.booking_email && (
            <p className="mt-8 text-sm text-white/70">
              BOOKING:{" "}
              <a
                data-testid="booking-direct-email"
                href={`mailto:${site.booking_email}`}
                className="text-white underline underline-offset-8 decoration-blood hover:text-bloodbright transition-colors break-all"
              >
                {site.booking_email}
              </a>
            </p>
          )}
        </div>
      </section>

      <form
        data-testid="booking-form"
        onSubmit={submit}
        className="px-5 md:px-10 mt-14 md:mt-20 max-w-4xl grid gap-8 md:grid-cols-2 md:gap-x-10"
        noValidate
      >
        <Field label="NAME" required>
          <input data-testid="booking-name" className="input-flush" value={form.name} onChange={set("name")} autoComplete="name" />
        </Field>
        <Field label="EMAIL" required>
          <input data-testid="booking-email" type="email" className="input-flush" value={form.email} onChange={set("email")} autoComplete="email" />
        </Field>
        <Field label="PHONE">
          <input data-testid="booking-phone" type="tel" className="input-flush" value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </Field>
        <Field label="COMPANY / PROMOTER">
          <input data-testid="booking-company" className="input-flush" value={form.company} onChange={set("company")} autoComplete="organization" />
        </Field>
        <Field label="EVENT NAME">
          <input data-testid="booking-event-name" className="input-flush" value={form.event_name} onChange={set("event_name")} />
        </Field>
        <Field label="EVENT DATE">
          <input data-testid="booking-event-date" type="date" className="input-flush" style={{ colorScheme: "dark" }} value={form.event_date} onChange={set("event_date")} />
        </Field>
        <Field label="VENUE">
          <input data-testid="booking-venue" className="input-flush" value={form.venue} onChange={set("venue")} />
        </Field>
        <Field label="CITY">
          <input data-testid="booking-city" className="input-flush" value={form.city} onChange={set("city")} />
        </Field>
        <Field label="COUNTRY">
          <input data-testid="booking-country" className="input-flush" value={form.country} onChange={set("country")} />
        </Field>
        <Field label="EXPECTED CAPACITY">
          <input data-testid="booking-capacity" className="input-flush" value={form.capacity} onChange={set("capacity")} inputMode="numeric" />
        </Field>
        <div className="md:col-span-2">
          <Field label="MESSAGE">
            <textarea data-testid="booking-message" className="input-flush min-h-[120px]" value={form.message} onChange={set("message")} />
          </Field>
        </div>

        <div className="md:col-span-2 flex items-start gap-4">
          <input
            data-testid="booking-consent"
            id="booking-consent"
            type="checkbox"
            checked={form.consent}
            onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
            className="mt-1 h-4 w-4 shrink-0 accent-[#7a0000]"
            required
          />
          <label htmlFor="booking-consent" className="text-xs text-white/50 leading-relaxed">
            I consent that my data from this form is stored and processed to handle my booking request
            (Art. 6 (1) (b) GDPR). It will not be shared with third parties.
          </label>
        </div>

        <div className="hidden" aria-hidden="true">
          <label>
            Leave empty
            <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
          </label>
        </div>

        {error && (
          <p data-testid="booking-error" className="md:col-span-2 text-sm text-red-400 border border-red-900/60 px-4 py-3">
            {error}
          </p>
        )}

        <div className="md:col-span-2">
          <button data-testid="booking-submit" type="submit" disabled={sending} className="btn-primary w-full sm:w-auto disabled:opacity-50">
            {sending ? "SENDING…" : "SEND BOOKING REQUEST"}
          </button>
        </div>
      </form>
    </div>
  );
}
